import AtlasSdk from '@atlas/sdk';

import useResponsiveValue from '@cobalt/react-responsive-value-hook';

import { WebFormConfigModel, WebFormConfigType, WebformLanguage } from '@/models/web-form.model';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  setCopyModalData,
  setCopyModalVisible,
  setPreviewModalData,
  setPreviewModalVisible,
} from '@/slices/web-form-config';

import { RELEASE_STAGE } from '@/config/env';
import { updateAppPath } from '@/config/routeAtlas';
import { RoutePathEnum } from '@/routes/constant';

type ResponsiveValues = 'small' | 'medium' | 'large';

export function useWebFormConfig() {
  const responsiveValue = useResponsiveValue<ResponsiveValues>(['small', 'medium', 'large']);

  const dispatch = useAppDispatch();
  const { previewModalVisible, previewModalData, copyModalVisible, copyModalData } = useAppSelector(
    (state) => state.webFormConfig,
  );

  const handleGoToPage = (to: 'list' | 'add' | 'edit', config?: WebFormConfigType) => {
    if (to === 'list') {
      updateAppPath({
        path: RoutePathEnum.ADMIN_ROOT,
        state: {
          website_url: config?.website_url,
          web_form_key: config?.web_form_key,
        },
      });
    } else {
      const path = `${RoutePathEnum.ADMIN_WEBFORMS}/${to}${config?.web_form_key ? `/${config?.web_form_key}` : ''}`;
      updateAppPath({ path });
    }
  };

  const getRegion = async () => {
    const { region } = await AtlasSdk.environment.getConfig();

    if (region.includes('us')) return 'us';
    if (region.includes('ca')) return 'ca';
    if (region.includes('eu')) return 'eu';
    if (region.includes('ap')) return 'au';
  };

  const genWebFormCode = async (web_form_key: string) => {
    const region = await getRegion();
    const isDev = RELEASE_STAGE === 'dev';
    const srcPrefix = `${window.location.origin}${!isDev ? '/case-management-ui/latest' : ''}`;
    const code = `<div id="cmWfContainer" style="margin: 0 auto; width: 100%; overflow: hidden;"></div>
<script>
  (function() {
    window.TD_CM_WEB_FORM_ENV = {
      key: "${web_form_key}",
      region: "${region}",
      release_stage: "${RELEASE_STAGE}",
      src_prefix: "${srcPrefix}",
      /*
       * Use 'auto' to make the container height adaptive to its content.
       * Or set a fixed value (e.g., '500px') to fix the container height.
       */
      container_height: "auto",
    };
    var scriptElement = document.createElement("script");
    scriptElement.src = "${srcPrefix}/web-form/index.js?_=" + Math.random();
    document.body.appendChild(scriptElement);
  })();
</script>`;
    return code;
  };

  const handleOpenPreviewModal = (id: string, language: WebformLanguage) => {
    if (!id || !language) return;
    dispatch(setPreviewModalData({ id, language }));
    dispatch(setPreviewModalVisible(true));
  };

  const handleClosePreviewModal = () => {
    dispatch(setPreviewModalVisible(false));
    setTimeout(() => {
      dispatch(setPreviewModalData(undefined));
    }, 500);
  };

  const handleOpenCopyModal = async (config: WebFormConfigModel) => {
    dispatch(
      setCopyModalData({
        website_url: config.website_url,
        code: await genWebFormCode(config.web_form_key),
      }),
    );
    dispatch(setCopyModalVisible(true));
  };

  const handleCloseCopyModal = () => {
    dispatch(setCopyModalVisible(false));
    setTimeout(() => {
      dispatch(setCopyModalData(undefined));
    }, 500);
  };

  return {
    responsiveValue,
    previewModalVisible,
    previewModalData,
    copyModalVisible,
    copyModalData,
    handleGoToPage,
    genWebFormCode,
    handleOpenPreviewModal,
    handleClosePreviewModal,
    handleOpenCopyModal,
    handleCloseCopyModal,
  };
}
